
import {
    Box, Button, Card, CardContent, Grid, Typography, TextField, Checkbox, MenuItem,
    Select,
    FormControl,
    InputLabel,
    ListSubheader,
    FormControlLabel,
} from "@mui/material"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers';
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import showToast from '../../../../components/Hot-Toast/showToast';
import dayjs from 'dayjs';
import { getDateTime, getDayOption, getMonthOption, getPayPeriods } from '../../../../utils/genericMethod';
import { useTQuery } from '../../../../utils/useTQuery';
import { CustomPagination } from '../../AdminComponents/CustomTable';
import DataTable from 'react-data-table-component';
import JobTimecardDetailsDialog from "../../../../components/DialogPopup/JobTimecardDetailsDialog";




export const TimecardAccounting = ({ selectedTab }) => {
    const [showResetButton, setShowResetButton] = useState(false);
    const payPeriods = useMemo(() => getPayPeriods(), []);
    const [period, setPeriod] = useState(null);
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [timecardDate, setTimecardDate] = useState('');
    const [customError, setCustomError] = useState('');
    const [selectedTimeCard, setSelectedTimeCard] = useState(null);
    const [timeData, setTimeData] = useState({
        inTime: null,
        outTime: null,
        autoFill: false,
    });
    const [timeCardSelectedStatus, setTimeCardSelectedStatus] = useState('All');
    const initialPayload = {
        timeCardStatus: 'All', dates: {}, times: {}, page: 1, rowsPerPage: 10
    }
    const [payload, setPayload] = useState(initialPayload);

    const { data, isLoading, isError, error, refetch } = useTQuery(
        ["timecard", payload, selectedTab],
        selectedTab ? `/job/timecard` : null,
        'post',
        payload,
        selectedTab?.text === 'TimeCard'
    );
    const [timeCardData, setTimeCardData] = useState([]);

    //Pagination 
    const [pagination, setPagination] = useState({
        page: 1,  // Zero-based for the first page
        rowsPerPage: 10,
        totalPage: 0,
    });

    useEffect(() => {
        if (data?.STATUS) {
            // console.log('pagination', data?.OUTPUT?.meta?.pageSize, data?.OUTPUT?.meta?.totalItems);
            setTimeCardData(data?.OUTPUT || []);
            setPagination(prev => ({
                ...prev,
                rowsPerPage: data?.meta?.pageSize ?? 10,
                totalPage: data?.meta?.totalItems ?? 0
            }));
        } else {
            setTimeCardData([]);
        }
    }, [selectedTab, data]);

    // pagination updates are handled inside the CustomPagination callbacks;
    // payload is updated there and react-query automatically refetches.  The
    // previous effect that called `refetch()` caused duplicate network calls so
    // we remove it.

    const timeCardColumn = [
        { name: 'Temp', selector: row => row.Temp, sortable: true },
        { name: 'Name', selector: row => row.Name, sortable: true },
        { name: 'Email', selector: row => row.EmailAddress, sortable: true },
        { name: 'Reconciled By', selector: row => row.ReconciledBy, sortable: true },
        { name: 'Shift Date', selector: row => new Date(row.ShiftDate).toDateString(), sortable: true },
        { name: 'Shift Time', selector: row => row.ShiftTime, sortable: true },
        { name: 'Status', selector: row => row.Status, sortable: true },
        {
            name: 'Timecards',
            button: true,
            center: true,
            cell: (row) => (
                <Button
                    variant="contained"
                    sx={{
                        backgroundColor: '#ccc',
                        color: '#000',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#bbb' }
                    }}
                    onClick={() => setSelectedTimeCard(row)}
                >
                    Open
                </Button>
            )
        }
    ];

    const handleSort = (column, sortDirection) => {
        // Map frontend column names to database field paths for sorting
        const fieldMapping = {
            'Temp': 'ID',
            'Name': 'user.FULL_NAME',
            'Email': 'user.EMAIL_ADDRESS',
            'Reconciled By': 'job.user.FACILITY_NAME',
            'Shift Date': 'job_service.JOB_DATE',
            'Shift Time': 'job_service.JOB_IN_TIME', // Sort by in-time as primary
            'Status': 'STATUS'
        };

        const fieldName = fieldMapping[column.name] || column.name;
        setPayload(prev => ({
            ...prev,
            FIELD: fieldName,
            DIRECTION: sortDirection.toUpperCase(),
            page: 1 // Reset to first page when sorting
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
        // no manual refetch – payload change will fire the query
    };
    const handleFilterClick = () => {
        const filter = getActiveFilter();
        if (filter.type === 'error') {
            setCustomError(filter.message);
            return;
        }
        const dates = getFilterDates();
        const times = getFilterTimes();
        if (dates || times) {
            setShowResetButton(true);
        }
        // if (!dates && !times) {
        //     showToast("error", "Please select a date or time filter.")
        //     return;
        // }
        const newPayload = {
            ...payload,
            timeCardStatus: timeCardSelectedStatus,
            dates,
            times,
        };

        setPayload(newPayload); // update state (query will rerun automatically)

    };

    const handleReset = () => {
        setPeriod(null);
        setCustomFrom('');
        setCustomTo('');
        setTimecardDate('');
        setTimeData({
            inTime: null,
            outTime: null,
            autoFill: false,
        });
        setPayload(initialPayload);
        setShowResetButton(false);
    };

    const handleAutoFill = (e) => {
        const isChecked = e.target.checked;
        setTimeData((prevState) => ({
            ...prevState,
            autoFill: isChecked,
            inTime: isChecked ? dayjs().set('hour', 9).set('minute', 0).set('second', 0) : null,
            outTime: isChecked ? dayjs().set('hour', 17).set('minute', 0).set('second', 0) : null,
        }));
    };
    const handleTimeChange = (newValue, field) => {
        if (field === 'outTime' && newValue.isBefore(timeData.inTime)) {
            // If outTime is before inTime, prevent the update and alert the user.
            showToast('error', 'End time must be later than start time.');
            return;
        }

        setTimeData((prevState) => ({
            ...prevState,
            [field]: newValue,
        }));
    };
    const getActiveFilter = () => {
        if (period) return { type: 'Period', message: 'Periods Filter is selected' };
        if (customFrom || customTo) {
            if (!customFrom || !customTo) {
                return { type: 'error', message: 'Select both Custom Date From and To' };
            }
            return { type: 'Custom Date', message: 'Custom Dates Filter is selected' };
        }
        if (timecardDate) return { type: 'Timecard', message: 'Timecard Dates Filter is selected' };
        return { type: null, message: '' };
    };
    const handlePeriodChange = (e) => {
        const value = JSON.parse(e.target.value);
        setPeriod(value);
        setCustomFrom('');
        setCustomTo('');
        setTimecardDate('');
        setCustomError('');
    };
    const handleCustomDateChange = (type, value) => {
        setPeriod(null);
        setTimecardDate('');
        setCustomError('');
        if (type === 'from') setCustomFrom(value);
        else setCustomTo(value);
    };
    const handleTimecardChange = (value) => {
        setTimecardDate(value);
        setCustomFrom('');
        setCustomTo('');
        setPeriod(null);
        setCustomError('');
    };
    const validateCustomDates = () => {
        setCustomError(''); // Reset first

        if (!customFrom || !customTo) {
            setCustomError('Select both Custom Date From and To');
            return;
        }

        const from = new Date(customFrom);
        const to = new Date(customTo);
        if (to < from) {
            setCustomError('"Custom To" date must be later than or equal to "Custom From" date');
            return;
        }
    };
    const getFilterDates = () => {
        if (period) {
            return { from: period.from, to: period.to };
        }
        if (customFrom && customTo) {

            const from = new Date(customFrom);
            const to = new Date(customTo);
            if (to < from) {
                setCustomError('"Custom To" date must be later than or equal to "Custom From" date');
                return;
            }
            return {
                from: getDateTime(from),
                to: getDateTime(to, true),
            };
        }
        if (timecardDate) {
            return {
                from: getDateTime(new Date('2000-01-01')),
                to: getDateTime(new Date(timecardDate), true),
            };
        }
        return null;
    };
    const getFilterTimes = () => {

        if (timeData?.inTime || timeData?.outTime) {
            return {
                inTime: timeData.inTime?.format("HH:mm") ?? null,
                outTime: timeData.outTime?.format("HH:mm") ?? null,
            };
        }

        return null;
    };
    const handleChange = (event) => {
        setTimeCardSelectedStatus(event.target.value);
    };
    const handleOpenClick = (row) => {
        console.log('Open clicked for:', row);
    };

    return (
        <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Timecard <span style={{ color: "#6ebe44" }}>Manager</span>
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="period-label">Period</InputLabel>
                        <Select
                            labelId="period-label"
                            value={period ? JSON.stringify(period) : ''}
                            label="Period"
                            onChange={handlePeriodChange}
                        >
                            <ListSubheader>Pay Period</ListSubheader>
                            {payPeriods.map((period, index) => (
                                <MenuItem key={`pay-${index}`} value={JSON.stringify(period)}>
                                    {period.label}
                                </MenuItem>
                            ))}

                            <ListSubheader>Days</ListSubheader>
                            {['Yesterday', 'Today', 'Tomorrow'].map((day) => {
                                const option = getDayOption(day);
                                return (
                                    <MenuItem key={day} value={JSON.stringify(option)}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}

                            <ListSubheader>Months</ListSubheader>
                            {['Last Month', 'This Month'].map((month) => {
                                const option = getMonthOption(month);
                                return (
                                    <MenuItem key={month} value={JSON.stringify(option)}>
                                        {option.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                {/* Custom Date From */}
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            fullWidth
                            label="Custom From"
                            type="date"
                            variant="outlined"
                            size="small"
                            InputProps={{
                                className: "picker_datefield"
                            }}
                            value={customFrom}
                            onChange={(e) => handleCustomDateChange('from', e.target.value)}
                            onBlur={validateCustomDates}
                            InputLabelProps={{ shrink: true }}
                            error={!!customError}
                        />
                    </FormControl>
                </Grid>

                {/* Custom Date To */}
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <TextField
                            fullWidth
                            label="Custom To"
                            type="date"
                            variant="outlined"
                            InputProps={{
                                className: "picker_datefield"
                            }}
                            size="small"
                            value={customTo}
                            onChange={(e) => handleCustomDateChange('to', e.target.value)}
                            onBlur={validateCustomDates}
                            InputLabelProps={{ shrink: true }}
                            error={!!customError}
                            helperText={customError}
                        />
                    </FormControl>
                </Grid>



                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Grid container spacing={2} mt={1} mb={2} px={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TimePicker
                                label="In Time"
                                value={timeData.inTime}
                                onChange={(newValue) => handleTimeChange(newValue, 'inTime')}

                                disabled={timeData.autoFill}

                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TimePicker
                                label="Out Time"
                                value={timeData.outTime}
                                onChange={(newValue) => handleTimeChange(newValue, 'outTime')}
                                disabled={timeData.autoFill}
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6} display="flex" alignItems="center">
                            <FormControlLabel
                                control={<Checkbox checked={timeData.autoFill} onChange={handleAutoFill} />}
                                label={<Typography>Auto Fill In/Out Time</Typography>}
                            />
                        </Grid>
                    </Grid>
                </LocalizationProvider>

                {/* Timecard Date */}
                <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                        <TextField
                            fullWidth
                            label="Timecard Submitted On or Before"
                            type="date"
                            variant="outlined"
                            InputProps={{
                                className: "picker_datefield"
                            }}
                            size="small"
                            value={timecardDate}
                            onChange={(e) => handleTimecardChange(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </FormControl>
                </Grid>

            </Grid>

            <Typography variant="h6" fontWeight="bold" mt={2}>
                Timecard <span style={{ color: "#6ebe44" }}>Filter</span>
            </Typography>

            {/* Checkbox */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2} display="flex" alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={timeCardSelectedStatus === 'All'}
                                onChange={() => handleChange({ target: { value: 'All' } })}
                            />
                        }
                        label={<Typography>All</Typography>}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3} display="flex" alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={timeCardSelectedStatus === 'Uploaded'}
                                onChange={() => handleChange({ target: { value: 'Uploaded' } })}
                            />
                        }
                        label={<Typography>Show Unreviewed</Typography>}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} display="flex" alignItems="center">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={timeCardSelectedStatus === 'Approved'}
                                onChange={() => handleChange({ target: { value: 'Approved' } })}
                            />
                        }
                        label={<Typography>Show Reconciled and Unpaid</Typography>}
                    />
                </Grid>

            </Grid>




            <Box textAlign="right" mt={2}>
                {showResetButton && <Button variant="outlined" onClick={handleReset}>
                    Reset
                </Button>}
                <Button variant="contained" sx={{ background: "grey", color: "#fff", textTransform: "none", ml: 1 }} onClick={handleFilterClick}>
                    Filter to Timecard Queue Below
                </Button>
            </Box>
            {/* Table */}
            <Box mt={2} sx={{ height: "100%", width: '100%' }}>

                <DataTable
                    className="table-items"
                    columns={timeCardColumn}
                    data={timeCardData}
                    sortServer
                    onSort={handleSort}
                    progressPending={isLoading}
                    progressComponent={<div>Loading timecards...</div>}
                />
                <CustomPagination
                    count={pagination.totalPage}
                    rowsPerPage={pagination.rowsPerPage}
                    page={pagination.page - 1}
                    setPage={(newPage) => {
                        setPagination(prev => ({ ...prev, page: newPage + 1 }));
                    }}
                    onChangePage={(newPage) => {
                        setPagination(prev => ({ ...prev, page: newPage + 1 }));
                        setPayload(prev => ({ ...prev, page: newPage + 1 }))
                    }}
                    onChangeRowsPerPage={(rowsPerPage) => {
                        setPagination(prev => ({ ...prev, rowsPerPage, page: 1 }));
                        setPayload(prev => ({ ...prev, rowsPerPage, page: 1 }))
                    }}
                />
            </Box>

            {selectedTimeCard && <JobTimecardDetailsDialog open={selectedTimeCard} onClose={() => setSelectedTimeCard(null)} data={selectedTimeCard} onSave={refetch} />}
        </CardContent>
    );
};






